import styled from '@emotion/styled';
import { css } from 'emotion';

import { getThemeColor } from '@ezugi/ui/theme';

export const BUTTON_HEIGHT = 32;
export const BORDER_RADIUS = 8.5;
export const FONT_SIZE = 10;
export const FONT_WEIGHT = '900';
export const ICON_SIZE = 16;

export const DENSE_BUTTON_HEIGHT = 22;
export const DENSE_BORDER_RADIUS = 6;
export const DENSE_FONT_SIZE = 12;
export const DENSE_FONT_WEIGHT = 'bold';
export const DENSE_ICON_SIZE = 14;

export const SPARSE_BUTTON_HEIGHT = 36;
export const SPARSE_BORDER_RADIUS = 8;
export const SPARSE_FONT_SIZE = 14;
export const SPARSE_FONT_WEIGHT = 'bold';
export const SPARSE_ICON_SIZE = 18;

const textStyles = css`
  font-family: Roboto;
  font-size: ${FONT_SIZE}px;
  font-weight: ${FONT_WEIGHT};
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: 1px;
  text-align: center;
  color: ${getThemeColor('primaryContrast')};
`;

export const Label = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: calc(100% - ${ICON_SIZE}px);
`;

const afterStyles = css`
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${BORDER_RADIUS}px;
`;

export const denseClassName = css`
  height: ${DENSE_BUTTON_HEIGHT}px;
  border-radius: ${DENSE_BORDER_RADIUS}px;
  font-size: ${DENSE_FONT_SIZE}px;
  font-weight: ${DENSE_FONT_WEIGHT};
  letter-spacing: normal;

  ${Label} {
    max-width: calc(100% - ${DENSE_ICON_SIZE}px);
  }

  :active:not([disabled]) {
    :after {
      border-radius: ${DENSE_BORDER_RADIUS}px;
    }
  }

  :hover:not(:active):not([disabled]) {
    :after {
      border-radius: ${DENSE_BORDER_RADIUS}px;
    }
  }
`;

export const sparseClassName = css`
  height: ${SPARSE_BUTTON_HEIGHT}px;
  border-radius: ${SPARSE_BORDER_RADIUS}px;
  font-size: ${SPARSE_FONT_SIZE}px;
  font-weight: ${SPARSE_FONT_WEIGHT};
  letter-spacing: normal;

  ${Label} {
    max-width: calc(100% - ${SPARSE_ICON_SIZE}px);
  }

  :active:not([disabled]) {
    :after {
      border-radius: ${SPARSE_BORDER_RADIUS}px;
    }
  }

  :hover:not(:active):not([disabled]) {
    :after {
      border-radius: ${SPARSE_BORDER_RADIUS}px;
    }
  }
`;

export const flexClassName = css`
  width: auto;
  flex: auto;
`;

export const transparentClassName = css`
  :not(:active) {
    :after {
      background: transparent !important;
    }
  }

  :not(:hover) {
    background: transparent !important;
    border-color: ${getThemeColor('primary')};
    color: ${getThemeColor('primary')};
    fill: ${getThemeColor('primary')};

    svg {
      color: ${getThemeColor('primary')};
      fill: ${getThemeColor('primary')};
    }
  }
`;
export const StyledButton = styled.button`
  -webkit-appearance: button;
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  user-select: none;
  outline: none;
  border-width: 1px;
  border-style: solid;
  border-color: transparent;
  border-radius: ${BORDER_RADIUS}px;
  background: ${getThemeColor('primary')};
  color: ${getThemeColor('primaryContrast')};
  fill: ${getThemeColor('primaryContrast')};
  height: ${BUTTON_HEIGHT}px;
  padding: 0 15px;
  ${textStyles};

  :not([disabled]) {
    cursor: pointer;
  }

  :active:not([disabled]) {
    :after {
      ${afterStyles};
      background: rgba(0, 0, 0, 0.15);
    }
  }

  :hover:not(:active):not([disabled]) {
    :after {
      ${afterStyles};
      background: rgba(255, 255, 255, 0.15);
    }
  }
`;

export const upperCaseLabel = css`
  text-transform: uppercase;
`;

export const iconClassName = css`
  > svg {
    color: ${getThemeColor('primaryContrast')};
    fill: ${getThemeColor('primaryContrast')};

    :hover {
      color: ${getThemeColor('primaryContrast')};
      fill: ${getThemeColor('primaryContrast')};
    }
  }
`;

export const disabledClassName = css`
  opacity: 0.4;
  cursor: not-allowed;
`;

type CustomClassnameProps = {
  primary: boolean;
  transparent: boolean;
  foreground?: string;
  background?: string;
  accent: boolean;
}

export const getButtonCusstomClassName = (
  { foreground, background, primary, accent, transparent }: CustomClassnameProps
) => {
  let bgColor = background;
  let fgColor = foreground;

  if (primary) {
    bgColor = getThemeColor('primary');
    fgColor = getThemeColor('primaryContrast');
  } else if (accent) {
    bgColor = getThemeColor('accent');
    fgColor = getThemeColor('accentContrast');
  }

  return css`
    background: ${bgColor};
    color: ${fgColor};

    svg {
      color: ${fgColor};
      fill: ${fgColor};

      :hover {
        color: ${fgColor};
        fill: ${fgColor};
      }
    }

    ${transparent
      && `
    :not(:hover) {
      svg {
        color: ${bgColor};
        fill: ${bgColor};
      }

      border-color: ${bgColor};
      color: ${bgColor};
      fill: ${bgColor};
    }`};
  `;
};
